/**
 * UserTokenService
 *
 * @author Copyright (c) 2021 Salesforce.com.
 * @ownership Appexchange Intelligence
 */
import { v4 as uuidv4 } from 'uuid';
import { validateUUID } from '~/modules/utilities/validation_utils';
import {
    CookieConsentCategory,
    createCatCookie,
    getCookieConsented,
    getCookieExpirationTimestamp,
    isConsentCategoryGranted,
} from '../utilities/cookies';

export const USER_TOKEN = 'appexchange_appxUserToken';
export const COOKIE_AGE_DAYS = 30;

export class UserTokenService {
    static getUserToken(): string {
        if (isConsentCategoryGranted(CookieConsentCategory.Functional)) {
            const userToken = getCookieConsented(USER_TOKEN, CookieConsentCategory.Functional);
            if (!userToken || !validateUUID(userToken)) {
                return this.setUserToken(this.getNewToken(), this.generateExpirationTimestamp());
            }
            return userToken;
        }
        return this.getLocalUserToken();
    }

    static setUserToken(userToken: string, expirationTimestamp?: number): string {
        const uuidUserToken = validateUUID(userToken) ? userToken : this.getNewToken();
        if (isConsentCategoryGranted(CookieConsentCategory.Functional)) {
            if (expirationTimestamp) {
                // @ts-expect-error * Automated Type Error Fix
                createCatCookie(USER_TOKEN, uuidUserToken, CookieConsentCategory.Functional, null, expirationTimestamp);
            } else {
                createCatCookie(USER_TOKEN, uuidUserToken, CookieConsentCategory.Functional, COOKIE_AGE_DAYS);
            }
        } else {
            const token = {
                userToken: uuidUserToken,
                expirationTimestamp: expirationTimestamp || this.generateExpirationTimestamp(),
            };
            localStorage.setItem(USER_TOKEN, JSON.stringify(token));
            return uuidUserToken;
        }
        return uuidUserToken;
    }

    static getNewToken(): string {
        return uuidv4();
    }

    static generateExpirationTimestamp() {
        const date = new Date();
        date.setTime(date.getTime() + COOKIE_AGE_DAYS * 24 * 60 * 60 * 1000);
        return date.getTime();
    }

    static async getTokenRegistrationParams() {
        const expirationTimestamp = await this.getUserTokenExpirationTimestamp();
        if (!expirationTimestamp) {
            /*
             * Existing production user tokens will have a null expiration value.
             * Generate a new token to use during login registration if the server
             * token has expired
             */
            return {
                userToken: UserTokenService.getNewToken(),
                expirationTimestamp: UserTokenService.generateExpirationTimestamp(),
            };
        }
        return { userToken: UserTokenService.getUserToken(), expirationTimestamp: expirationTimestamp };
    }

    static async getUserTokenExpirationTimestamp() {
        try {
            if (isConsentCategoryGranted(CookieConsentCategory.Functional)) {
                return getCookieExpirationTimestamp(USER_TOKEN);
            }
            const rawLocalUserToken = localStorage.getItem(USER_TOKEN);
            // @ts-expect-error * Automated Type Error Fix
            const localUserToken = JSON.parse(rawLocalUserToken);
            return localUserToken.expirationTimestamp;
        } catch (e) {
            return null;
        }
    }

    static getLocalUserToken(): string {
        try {
            const rawLocalUserToken = localStorage.getItem(USER_TOKEN);
            // @ts-expect-error * Automated Type Error Fix
            const localUserToken = JSON.parse(rawLocalUserToken);
            const currentTime = new Date().getTime();
            if (currentTime > localUserToken.expirationTimestamp || !validateUUID(localUserToken.userToken)) {
                return this.setUserToken(this.getNewToken(), this.generateExpirationTimestamp());
            }
            return localUserToken.userToken;
        } catch (e) {
            return this.setUserToken(this.getNewToken(), this.generateExpirationTimestamp());
        }
    }
}
